import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCorporateDocuments, CorporateDocument, DocumentCategory } from "@/hooks/useCorporateDocuments";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PDFPreview } from "@/components/corporate-documents/PDFPreview";
import { CategoryTabs, getCategoryLabel, getCategoryIcon } from "@/components/corporate-documents/CategoryTabs";
import { 
  BookOpen, 
  Upload, 
  FileText, 
  ExternalLink, 
  Plus,
  Trash2,
  Calendar,
  User,
  Loader2,
  File,
  Eye,
  Search
} from "lucide-react";

const translations = {
  en: {
    title: "Corporate Documents",
    subtitle: "Training guides, policies and procedures for team members",
    referenceGuide: "Platform Reference Guide",
    referenceDesc: "Complete documentation of the AcquizGroup platform for freelancers and business partners",
    viewGuide: "View Guide",
    addDocument: "Add Document",
    documentTitle: "Document Title",
    documentDesc: "Description",
    documentUrl: "Document URL (optional if uploading file)",
    documentType: "Type",
    documentCategory: "Category",
    typeGuide: "Guide",
    typePolicy: "Policy",
    typeProcedure: "Procedure",
    typeTraining: "Training",
    save: "Save",
    cancel: "Cancel",
    noDocuments: "No documents in this category",
    addedBy: "Added by",
    delete: "Delete",
    confirmDelete: "Are you sure you want to delete this document?",
    uploadFile: "Upload PDF File",
    selectFile: "Select file",
    orEnterUrl: "Or enter URL manually",
    uploading: "Uploading...",
    loading: "Loading documents...",
    fileSelected: "File selected",
    preview: "Preview",
    openNew: "Open",
    searchPlaceholder: "Search documents...",
  },
  fr: {
    title: "Documents Corporatifs",
    subtitle: "Guides de formation, politiques et procédures pour les membres de l'équipe",
    referenceGuide: "Guide de Référence Plateforme",
    referenceDesc: "Documentation complète de la plateforme AcquizGroup pour freelancers et partenaires d'affaires",
    viewGuide: "Voir le Guide",
    addDocument: "Ajouter un Document",
    documentTitle: "Titre du Document",
    documentDesc: "Description",
    documentUrl: "URL du Document (optionnel si fichier uploadé)",
    documentType: "Type",
    documentCategory: "Catégorie",
    typeGuide: "Guide",
    typePolicy: "Politique",
    typeProcedure: "Procédure",
    typeTraining: "Formation",
    save: "Enregistrer",
    cancel: "Annuler",
    noDocuments: "Aucun document dans cette catégorie",
    addedBy: "Ajouté par",
    delete: "Supprimer",
    confirmDelete: "Êtes-vous sûr de vouloir supprimer ce document?",
    uploadFile: "Uploader un fichier PDF",
    selectFile: "Sélectionner un fichier",
    orEnterUrl: "Ou entrer l'URL manuellement",
    uploading: "Téléchargement...",
    loading: "Chargement des documents...",
    fileSelected: "Fichier sélectionné",
    preview: "Aperçu",
    openNew: "Ouvrir",
    searchPlaceholder: "Rechercher des documents...",
  },
};

export default function CorporateDocuments() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = translations[language as keyof typeof translations] || translations.en;
  
  const { documents, loading, uploading, addDocument, deleteDocument } = useCorporateDocuments();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'all'>('all');
  const [previewDoc, setPreviewDoc] = useState<CorporateDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newDoc, setNewDoc] = useState({
    title: "",
    description: "",
    url: "",
    type: "guide" as CorporateDocument['type'],
    category: "general" as DocumentCategory,
  });

  const isAdmin = user?.roles?.includes("admin");

  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = searchQuery === "" || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleAddDocument = async () => {
    const success = await addDocument({
      ...newDoc,
      file: selectedFile || undefined,
    });

    if (success) {
      setNewDoc({ title: "", description: "", url: "", type: "guide", category: "general" });
      setSelectedFile(null);
      setIsDialogOpen(false);
    }
  };

  const handleDeleteDocument = async (doc: CorporateDocument) => {
    if (confirm(t.confirmDelete)) {
      await deleteDocument(doc);
    }
  };

  const getTypeBadgeColor = (type: CorporateDocument['type']) => {
    switch (type) {
      case 'guide': return 'bg-blue-500';
      case 'policy': return 'bg-purple-500';
      case 'procedure': return 'bg-green-500';
      case 'training': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeLabel = (type: CorporateDocument['type']) => {
    switch (type) {
      case 'guide': return t.typeGuide;
      case 'policy': return t.typePolicy;
      case 'procedure': return t.typeProcedure;
      case 'training': return t.typeTraining;
      default: return type;
    }
  };

  const isPdfUrl = (url: string) => {
    return url.includes('.pdf') || url.includes('firebasestorage');
  };

  const isExternalUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t.title}</h1>
          <p className="text-gray-400 mt-1">{t.subtitle}</p>
        </div>
        {isAdmin && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                <Plus className="h-4 w-4 mr-2" />
                {t.addDocument}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle>{t.addDocument}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{t.documentTitle}</Label>
                  <Input 
                    value={newDoc.title}
                    onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label>{t.documentDesc}</Label>
                  <Textarea 
                    value={newDoc.description}
                    onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                
                {/* File Upload Section */}
                <div className="space-y-2">
                  <Label>{t.uploadFile}</Label>
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2">
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t.selectFile}
                    </Button>
                    {selectedFile && (
                      <span className="text-sm text-green-400 flex items-center gap-1">
                        <File className="h-4 w-4" />
                        {selectedFile.name}
                      </span>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-600" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-gray-800 px-2 text-gray-400">{t.orEnterUrl}</span>
                  </div>
                </div>

                <div>
                  <Label>{t.documentUrl}</Label>
                  <Input 
                    value={newDoc.url}
                    onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
                    placeholder="https:// or /docs/..."
                    className="bg-gray-700 border-gray-600 text-white"
                    disabled={!!selectedFile}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t.documentType}</Label>
                    <select
                      value={newDoc.type}
                      onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value as CorporateDocument['type'] })}
                      className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    >
                      <option value="guide">{t.typeGuide}</option>
                      <option value="policy">{t.typePolicy}</option>
                      <option value="procedure">{t.typeProcedure}</option>
                      <option value="training">{t.typeTraining}</option>
                    </select>
                  </div>
                  <div>
                    <Label>{t.documentCategory}</Label>
                    <select
                      value={newDoc.category}
                      onChange={(e) => setNewDoc({ ...newDoc, category: e.target.value as DocumentCategory })}
                      className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    >
                      <option value="hr">{language === 'fr' ? 'RH' : 'HR'}</option>
                      <option value="training">{language === 'fr' ? 'Formation' : 'Training'}</option>
                      <option value="procedures">{language === 'fr' ? 'Procédures' : 'Procedures'}</option>
                      <option value="legal">{language === 'fr' ? 'Légal' : 'Legal'}</option>
                      <option value="general">{language === 'fr' ? 'Général' : 'General'}</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t.cancel}
                  </Button>
                  <Button 
                    onClick={handleAddDocument} 
                    className="bg-amber-500 hover:bg-amber-600"
                    disabled={uploading || (!newDoc.url && !selectedFile) || !newDoc.title}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t.uploading}
                      </>
                    ) : (
                      t.save
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Main Reference Guide Card */}
      <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-amber-500 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-white text-xl">{t.referenceGuide}</CardTitle>
              <CardDescription className="text-gray-300">{t.referenceDesc}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Link to="/docs/reference">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                <ExternalLink className="h-4 w-4 mr-2" />
                {t.viewGuide}
              </Button>
            </Link>
            <Badge className="bg-blue-500">{t.typeGuide}</Badge>
            <span className="text-sm text-gray-400">v1.1 - Janvier 2025</span>
          </div>
        </CardContent>
      </Card>

      {/* Category Tabs and Search */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <CategoryTabs 
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          language={language}
        />
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="pl-9 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          <span className="ml-2 text-gray-400">{t.loading}</span>
        </div>
      )}

      {/* Additional Documents Grid */}
      {!loading && filteredDocuments.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredDocuments.map((doc) => {
            const CategoryIcon = getCategoryIcon(doc.category);
            return (
              <Card key={doc.id} className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <CardTitle className="text-white text-base">{doc.title}</CardTitle>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge className={getTypeBadgeColor(doc.type)}>
                        {getTypeLabel(doc.type)}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <CategoryIcon className="h-3 w-3" />
                    {getCategoryLabel(doc.category, language)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-400 line-clamp-2">{doc.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {doc.created_at}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {doc.created_by}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      {isPdfUrl(doc.url) && isExternalUrl(doc.url) && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setPreviewDoc(doc)}
                          className="text-gray-300 border-gray-600 hover:bg-gray-700"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          {t.preview}
                        </Button>
                      )}
                      {isExternalUrl(doc.url) ? (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="text-gray-300 border-gray-600 hover:bg-gray-700">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            {t.openNew}
                          </Button>
                        </a>
                      ) : (
                        <Link to={doc.url}>
                          <Button size="sm" variant="outline" className="text-gray-300 border-gray-600 hover:bg-gray-700">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            {t.viewGuide}
                          </Button>
                        </Link>
                      )}
                    </div>
                    {isAdmin && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDeleteDocument(doc)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && filteredDocuments.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{t.noDocuments}</p>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewDoc && (
        <PDFPreview
          url={previewDoc.url}
          title={previewDoc.title}
          isOpen={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}
